import 'react-quill-new/dist/quill.snow.css';
import React, { useState, useRef, useEffect, useCallback } from "react";
import StreamText from "./StreamText";
import axios from "axios";
import Loader from "./Loader";
import { v4 as uuidv4 } from "uuid";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppContext } from "../Context/AppProvider";
import { FaShieldAlt } from "react-icons/fa";
// import useAppContext from "../Context/AppProvider";


const termsAndConditionsText = `
<p><em>Effective Date: 10/1/2025</em></p>
<p>Welcome to <strong>SmartStart AI</strong>, part of the 10 Minute Cashflow program operated by <strong>Free & Happy Inc.</strong> ("Company," "we," "us," or "our"). By using SmartStart AI, you agree to these Terms and Conditions ("Agreement"). If you do not agree, do not use SmartStart AI.</p>
<p><strong>1. Purpose of SmartStart AI</strong><br />
SmartStart AI is an educational tool designed to create a personalized cashflow plan based on your answers to a short series of questions. It is intended for informational purposes only and does not guarantee income, business success, or financial results.</p>
<p><strong>2. Data Collection and Use</strong><br />
By using SmartStart AI, you agree that we may collect and process the information you provide, including your name, email address, location, demographic details, and your responses to intake questions. This information may be used for:<br />
• Delivering your personalized Cashflow Plan via email<br />
• Adding you to marketing email sequences about 10 Minute Cashflow and related products<br />
• Using advertising and email platforms to deliver marketing messages (e.g., Meta, Google, and email service providers we control)<br />
• Improving SmartStart AI and developing new educational tools and services<br />
We do not sell your personal data to third parties.</p>
<p><strong>3. Data Storage & Retention</strong><br />
Your information may be stored on our systems, our technology partners' platforms, or third-party cloud providers. We may retain your data indefinitely unless you request deletion. You can unsubscribe from marketing emails at any time by clicking the "unsubscribe" link in our messages.</p>
<p><strong>4. Intellectual Property</strong><br />
All content generated by SmartStart AI (including your personalized plan) remains the property of Free & Happy Inc. You are granted a limited, non-exclusive, non-transferable license to use the content for your personal, non-commercial use. You may not resell, reproduce, or redistribute SmartStart AI outputs without our written permission.</p>
<p><strong>5. No Guarantees or Warranties</strong><br />
SmartStart AI is provided "as is." Free & Happy Inc. makes no guarantees or warranties regarding accuracy, completeness, or usefulness. Your use of SmartStart AI is voluntary and at your own risk. We do not guarantee that you will earn any income or achieve any specific financial results.</p>
<p><strong>6. Modifications</strong><br />
We may update or discontinue SmartStart AI at any time, and we may modify these Terms without notice. Continued use after changes means you accept the updated Terms.</p>
<p><strong>7. Limitation of Liability</strong><br />
To the fullest extent permitted by law, Free & Happy Inc. is not liable for any indirect, incidental, consequential, or special damages arising from your use of SmartStart AI.</p>
<p><strong>8. Governing Law & Dispute Resolution</strong><br />
This Agreement shall be governed by the laws of the State of Utah, without regard to conflict of law rules. Any disputes shall be resolved through binding arbitration in St. George, Utah.</p>
<p><strong>9. Privacy Policy</strong><br />
For more details on how we handle your data, please see our Privacy Policy:<br />
<a href="https://jakeducey.com/privacy-policy/" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">https://jakeducey.com/privacy-policy/</a></p>
<p><strong>10. Contact Information</strong><br />
If you have questions about these Terms, contact us at: <a href="mailto:jd@jakeducey.com" class="text-blue-600 hover:text-blue-800 underline">jd@jakeducey.com</a></p>
`;

// Helper function to check if text contains HTML tags
const formatHtmlForDisplay = (content) =>
  content?.replace(/&nbsp;/g, " ")?.replace(/\u00A0/g, " ") ?? "";

const containsHTML = (text) => {
  if (!text || typeof text !== 'string') return false;
  // Check for HTML tags (opening or closing tags)
  const htmlTagRegex = /<\/?[a-z][\s\S]*>/i;
  return htmlTagRegex.test(text);
};

const ChatWindow = () => {
  const { fetchSessions = () => {} } = useAppContext() || {};
  const [guestId, setGuestId] = useState(
    () => localStorage.getItem("guestId") || null
  );
  const userData = localStorage.getItem("userData");
  const recommendationInitiatedRef = useRef(false);
  const prevRouteSessionIdRef = useRef();
  const { sessionId: routeSessionId } = useParams();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(true);
  const [autoScroll, setAutoScroll] = useState(true);
  const [isMessageChanged, setIsMessageChanged] = useState("");
  const [recommendedLesson, setRecommendedLesson] = useState(null);
  const [bonusLessons, setBonusLessons] = useState(null); // NEW STATE FOR BONUS LESSONS
  const [isRecommending, setIsRecommending] = useState(false); // For recommendation API call
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false); // For PDF API call
  const [recommendationProgress, setRecommendationProgress] = useState(0); // Progress percentage for recommendations
  const [progressMessage, setProgressMessage] = useState(""); // Progress message
  const [questions, setQuestions] = useState([]);
  console.log("🚀 ~ ChatWindow ~ questions:", questions);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentSessionId, setCurrentSessionId] = useState(
    routeSessionId || null
  );
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [enableLessonHyperlinks, setEnableLessonHyperlinks] = useState(true); // Default to true
  const [lessonCountSettings, setLessonCountSettings] = useState({
    l1LessonCount: null, // null means no limit
    l2LessonCount: null,
    l3LessonCount: null,
  });
  const [chatTextSettings, setChatTextSettings] = useState({
    l1IntroText: '',
    l2Explanation: '',
    l3Explanation: '',
    finalChatText: '',
    bonusLessonsHeaderText: '',
    pdfDownloadMessage: '',
    finalRedirectUrl: '',
  });
  const [enablePdfDownload, setEnablePdfDownload] = useState(true); // Default to true
  const [showSummaryBelowTitle, setShowSummaryBelowTitle] = useState(false); // Default to false

  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const stableFetchSessions = useCallback(fetchSessions, [fetchSessions]);
  const API_BASE_URL_ADMIN = `${process.env.REACT_APP_BACKEND_URL}/api/v1/admin`;
  const API_BASE_URL_USER = `${process.env.REACT_APP_BACKEND_URL}/api/v1/user`;

  const isTermsAcceptedForSession = (sessionId) => {
    if (!sessionId) return false;
    try {
      const acceptedSessions = JSON.parse(
        localStorage.getItem("acceptedSessions") || "{}"
      );
      return acceptedSessions[sessionId] === true;
    } catch (e) {
      console.error("Failed to parse acceptedSessions from localStorage", e);
      return false;
    }
  };

  const setTermsAcceptedForSessionStorage = (sessionId) => {
    if (!sessionId) return;
    try {
      const acceptedSessions = JSON.parse(
        localStorage.getItem("acceptedSessions") || "{}"
      );
      acceptedSessions[sessionId] = true;
      localStorage.setItem(
        "acceptedSessions",
        JSON.stringify(acceptedSessions)
      );
    } catch (e) {
      console.error("Failed to save acceptedSessions to localStorage", e);
    }
  };

  useEffect(() => {
    if (routeSessionId) {
      if (isTermsAcceptedForSession(routeSessionId)) {
        setTermsAccepted(true);
      } else {
        setTermsAccepted(false);
        setMessages([]);
        setCurrentQuestionIndex(0);
        setRecommendedLesson(null);
        setBonusLessons(null); // RESET BONUS LESSONS
        recommendationInitiatedRef.current = false;
      }
      setCurrentSessionId(routeSessionId);
    } else {
      setTermsAccepted(false);
      setCurrentSessionId(null);
      setMessages([]);
      setCurrentQuestionIndex(0);
      setRecommendedLesson(null);
      setBonusLessons(null); // RESET BONUS LESSONS
      recommendationInitiatedRef.current = false;
    }
  }, [routeSessionId]);

  async function fetchWithRetry(apiCall, retries = 2, delay = 1000) {
    try {
      return await apiCall();
    } catch (error) {
      if (retries > 0) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        return fetchWithRetry(apiCall, retries - 1, delay * 2); // Exponential backoff
      }
      throw error; // Rethrow after exhausting retries
    }
  }

  // Fetch settings (hyperlinks and lesson counts) - fetch immediately on mount and when terms are accepted
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        console.log("🔗 Fetching settings from:", `${API_BASE_URL_ADMIN}/get-settings`);
        const response = await axios.post(
          `${API_BASE_URL_ADMIN}/get-settings`
        );
        console.log("🔗 Settings response:", response.data);
        
        const settingsData = response.data?.data || {};
        
        // Handle hyperlinks setting
        const settingValue = settingsData.allowLessonHyperlinks;
        console.log("🔗 Hyperlinks setting value:", settingValue, "Type:", typeof settingValue);
        
        if (settingValue === false || settingValue === "false" || settingValue === 0) {
          console.log("🔗 Setting hyperlinks to FALSE");
          setEnableLessonHyperlinks(false);
        } else if (settingValue === true || settingValue === "true" || settingValue === 1) {
          console.log("🔗 Setting hyperlinks to TRUE");
          setEnableLessonHyperlinks(true);
        } else {
          console.log("🔗 Setting hyperlinks to TRUE (default)");
          setEnableLessonHyperlinks(true);
        }
        

        
        // Handle lesson count settings
        const l1Count = settingsData.l1LessonCount;
        const l2Count = settingsData.l2LessonCount;
        const l3Count = settingsData.l3LessonCount;
        
        console.log("📊 Lesson count settings:", { l1Count, l2Count, l3Count });
        
        setLessonCountSettings({
          l1LessonCount: l1Count !== undefined && l1Count !== null ? Number(l1Count) : null,
          l2LessonCount: l2Count !== undefined && l2Count !== null ? Number(l2Count) : null,
          l3LessonCount: l3Count !== undefined && l3Count !== null ? Number(l3Count) : null,
        });
        
        // Handle chat text settings
        setChatTextSettings({
          l1IntroText: settingsData.l1IntroText || '',
          l2Explanation: settingsData.l2Explanation || '',
          l3Explanation: settingsData.l3Explanation || '',
          finalChatText: settingsData.finalChatText || '',
          bonusLessonsHeaderText: settingsData.bonusLessonsHeaderText || '',
          pdfDownloadMessage: settingsData.pdfDownloadMessage || '',
          finalRedirectUrl: settingsData.finalRedirectUrl || '',
        });
        
        // Handle PDF download setting
        const pdfDownloadValue = settingsData.enablePdfDownload;
        if (pdfDownloadValue === false || pdfDownloadValue === "false" || pdfDownloadValue === 0) {
          setEnablePdfDownload(false);
        } else if (pdfDownloadValue === true || pdfDownloadValue === "true" || pdfDownloadValue === 1) {
          setEnablePdfDownload(true);
        } else {
          // Default to true if null/undefined
          setEnablePdfDownload(true);
        }
        
        // Handle showSummaryBelowTitle setting
        const showSummaryValue = settingsData.showSummaryBelowTitle;
        if (showSummaryValue === true || showSummaryValue === "true" || showSummaryValue === 1) {
          setShowSummaryBelowTitle(true);
        } else if (showSummaryValue === false || showSummaryValue === "false" || showSummaryValue === 0) {
          setShowSummaryBelowTitle(false);
        } else {
          // Default to false if null/undefined
          setShowSummaryBelowTitle(false);
        }
      } catch (error) {
        console.error("❌ Error fetching settings:", error);
        console.error("❌ Error details:", error.response?.data || error.message);
        // Default to true on error
        setEnableLessonHyperlinks(true);
      }
    };
    
    // Fetch immediately when component mounts
    fetchSettings();
    
    // Also refetch periodically to get latest settings (every 10 seconds)
    const interval = setInterval(fetchSettings, 10000);
    
    return () => clearInterval(interval);
  }, [API_BASE_URL_ADMIN]);
  
  // Also refetch when terms are accepted (in case settings changed while user was on terms page)
  useEffect(() => {
    if (termsAccepted) {
      const fetchSettings = async () => {
        try {
          const response = await axios.post(
            `${API_BASE_URL_ADMIN}/get-settings`
          );
          const settingsData = response.data?.data || {};
          const settingValue = settingsData.allowLessonHyperlinks;
          console.log("🔗 Refetching after terms accepted - setting value:", settingValue);
          
          if (settingValue === false || settingValue === "false" || settingValue === 0) {
            setEnableLessonHyperlinks(false);
          } else if (settingValue === true || settingValue === "true" || settingValue === 1) {
            setEnableLessonHyperlinks(true);
          } else {
            setEnableLessonHyperlinks(true);
          }
          
          // Update lesson count settings
          setLessonCountSettings({
            l1LessonCount: settingsData.l1LessonCount !== undefined && settingsData.l1LessonCount !== null ? Number(settingsData.l1LessonCount) : null,
            l2LessonCount: settingsData.l2LessonCount !== undefined && settingsData.l2LessonCount !== null ? Number(settingsData.l2LessonCount) : null,
            l3LessonCount: settingsData.l3LessonCount !== undefined && settingsData.l3LessonCount !== null ? Number(settingsData.l3LessonCount) : null,
          });
          
          // Update chat text settings
          setChatTextSettings({
            l1IntroText: settingsData.l1IntroText || '',
            l2Explanation: settingsData.l2Explanation || '',
            l3Explanation: settingsData.l3Explanation || '',
            finalChatText: settingsData.finalChatText || '',
            bonusLessonsHeaderText: settingsData.bonusLessonsHeaderText || '',
            pdfDownloadMessage: settingsData.pdfDownloadMessage || '',
            finalRedirectUrl: settingsData.finalRedirectUrl || '',
          });
          
          // Update PDF download setting
          const pdfDownloadValue = settingsData.enablePdfDownload;
          if (pdfDownloadValue === false || pdfDownloadValue === "false" || pdfDownloadValue === 0) {
            setEnablePdfDownload(false);
          } else if (pdfDownloadValue === true || pdfDownloadValue === "true" || pdfDownloadValue === 1) {
            setEnablePdfDownload(true);
          } else {
            setEnablePdfDownload(true);
          }
          
          // Update showSummaryBelowTitle setting
          const showSummaryValue = settingsData.showSummaryBelowTitle;
          if (showSummaryValue === true || showSummaryValue === "true" || showSummaryValue === 1) {
            setShowSummaryBelowTitle(true);
          } else if (showSummaryValue === false || showSummaryValue === "false" || showSummaryValue === 0) {
            setShowSummaryBelowTitle(false);
          } else {
            setShowSummaryBelowTitle(false);
          }
        } catch (error) {
          console.error("❌ Error fetching settings after terms accepted:", error);
        }
      };
      fetchSettings();
    }
  }, [termsAccepted, API_BASE_URL_ADMIN]);

  useEffect(() => {
    const fetchApiQuestions = async () => {
      if (!termsAccepted) return;
      setIsLoadingQuestions(true);
      try {
        const response = await fetchWithRetry(() =>
          axios.get(`${API_BASE_URL_ADMIN}/questions`)
        );
        if (response.data && Array.isArray(response.data.questions)) {
          const formattedQuestions = response.data.questions.map((q) => ({
            id: q._id,
            system: q.question.replace(/\\n/g, "\n"),
          }));
          setQuestions(formattedQuestions);
        } else {
          setQuestions([]);
          toast.error(
            "Could not load questions: Invalid data format received."
          );
        }
      } catch (error) {
        console.error("Error fetching questions from API:", error);
        toast.error(
          "Failed to load questions. Please refresh or try again later."
        );
        setQuestions([]);
      } finally {
        setIsLoadingQuestions(false);
      }
    };
    fetchApiQuestions();
  }, [termsAccepted, API_BASE_URL_ADMIN]);

  const handleAcceptTerms = () => {
    let sessionIdToUse = routeSessionId || currentSessionId;
    if (!sessionIdToUse) {
      sessionIdToUse = uuidv4();
      if (!routeSessionId) {
        window.history.pushState({}, "", `/chat/${sessionIdToUse}`);
      }
    }
    setCurrentSessionId(sessionIdToUse);
    setTermsAccepted(true);
    setTermsAcceptedForSessionStorage(sessionIdToUse);
  };

  useEffect(() => {
    if (prevRouteSessionIdRef.current !== routeSessionId) {
      recommendationInitiatedRef.current = false;
    }
    prevRouteSessionIdRef.current = routeSessionId;

    const initOrFetchConversation = async (sessionIdToFetch) => {
      setMessages([]);
      setIsLoading(true); // General loading for conversation
      try {
        if (!sessionIdToFetch) {
          if (questions.length > 0) {
            setMessages([
              { type: "bot", text: questions[0].system, isStreaming: true },
            ]);
            setCurrentQuestionIndex(0);
          } else {
            setMessages([
              {
                type: "bot",
                text: "Welcome! Questions are not available at the moment. Please try refreshing.",
                isStreaming: false,
              },
            ]);
          }
        } else {
          const res = await fetchWithRetry(() =>
            axios.get(
              `${API_BASE_URL_USER}/get-conversation/${sessionIdToFetch}`
            )
          );
          const responseData = res.data?.responseData;

          if (
            !responseData ||
            !responseData.conversation ||
            responseData.conversation.length === 0
          ) {
            if (questions.length > 0) {
              setMessages([
                { type: "bot", text: questions[0].system, isStreaming: true },
              ]);
              setCurrentQuestionIndex(0);
            } else {
              setMessages([
                {
                  type: "bot",
                  text: "Welcome! No prior conversation found. Questions are not available. Please try refreshing.",
                  isStreaming: false,
                },
              ]);
            }
          } else {
            const fetchedConvoItems = responseData.conversation;
            const newLoadedMessages = [];
            let answeredCount = 0;
            for (const item of fetchedConvoItems) {
              newLoadedMessages.push({
                type: "bot",
                text: item.system,
                isStreaming: false,
              });
              if (item.user != null) {
                newLoadedMessages.push({ type: "user", text: item.user });
                answeredCount++;
              } else break;
            }
            setMessages(newLoadedMessages);
            setCurrentQuestionIndex(answeredCount);
            if (stableFetchSessions) stableFetchSessions();

            const isQuestionnairePreviouslyCompleted =
              responseData.isComplete ||
              (questions.length > 0 && answeredCount >= questions.length);

            if (isQuestionnairePreviouslyCompleted) {
              if (
                !recommendationInitiatedRef.current &&
                !isRecommending &&
                !isGeneratingPdf
              ) {
                recommendationInitiatedRef.current = true;
                setTimeout(
                  () => fetchAndRecommendLesson(sessionIdToFetch, true),
                  50
                );
              }
            } else if (
              questions.length > 0 &&
              answeredCount < questions.length
            ) {
              const lastUserMsg =
                newLoadedMessages[newLoadedMessages.length - 1]?.type ===
                "user";
              if (lastUserMsg && questions[answeredCount]) {
                setMessages((prev) => [
                  ...prev,
                  {
                    type: "bot",
                    text: questions[answeredCount].system,
                    isStreaming: true,
                  },
                ]);
              }
            } else if (
              answeredCount > 0 &&
              questions.length === 0 &&
              !isQuestionnairePreviouslyCompleted
            ) {
              setMessages((prev) => [
                ...prev,
                {
                  type: "bot",
                  text: "Your previous conversation has been loaded, but the questionnaire structure is currently unavailable.",
                  isStreaming: false,
                },
              ]);
            }
          }
        }
      } catch (err) {
        console.error("Error fetching or initializing conversation:", err);
        toast.error(
          "Failed to load conversation. Starting a new chat if possible."
        );
        if (questions.length > 0)
          setMessages([
            { type: "bot", text: questions[0].system, isStreaming: true },
          ]);
        else
          setMessages([
            {
              type: "bot",
              text: "Sorry, an error occurred. Unable to start chat.",
              isStreaming: false,
            },
          ]);
      } finally {
        setIsLoading(false);
      }
    };

    if (termsAccepted && !isLoadingQuestions && currentSessionId) {
      if (
        messages.length === 0 ||
        (routeSessionId && routeSessionId !== prevRouteSessionIdRef.current)
      ) {
        initOrFetchConversation(currentSessionId);
      }
    } else if (
      termsAccepted &&
      !isLoadingQuestions &&
      !currentSessionId &&
      questions.length > 0
    ) {
      initOrFetchConversation(null);
    }
  }, [
    currentSessionId,
    termsAccepted,
    isLoadingQuestions,
    questions,
    routeSessionId,
    stableFetchSessions,
    isRecommending,
    isGeneratingPdf,
  ]);

  const handleSend = async () => {
    const userInput = input.trim();
    if (
      !userInput ||
      !termsAccepted ||
      isLoading ||
      isRecommending ||
      isGeneratingPdf ||
      (recommendedLesson && recommendedLesson.length > 0)
    ) {
      return;
    }

    const currentQ = questions && questions[currentQuestionIndex];
    if (!currentQ) {
      if (!recommendedLesson || recommendedLesson.length === 0) {
        toast.info(
          "Questionnaire finished or no questions available. Recommendations might be pending or failed."
        );
      }
      return;
    }

    setInput("");
    let sessionIdToUse = currentSessionId;
    if (!sessionIdToUse) {
      sessionIdToUse = uuidv4();
      if (!routeSessionId)
        window.history.pushState({}, "", `/chat/${sessionIdToUse}`);
      setCurrentSessionId(sessionIdToUse);
      setTermsAcceptedForSessionStorage(sessionIdToUse);
    }

    setMessages((prev) => [
      ...prev.filter((msg) => !(msg.type === "bot" && msg.isLoading)),
      { type: "user", text: userInput },
    ]);
    setMessages((prev) => [
      ...prev,
      { type: "bot", isLoading: true, text: "" },
    ]); // General bot thinking loader
    setIsLoading(true); // For chat send

    const currentSystemPrompt = currentQ.system;
    const payload = {
      content: userInput,
      sessionId: sessionIdToUse,
      userId: JSON.parse(userData)?.id,
      prompt: currentSystemPrompt,
    };

    axios
      .post(`${API_BASE_URL_USER}/chat`, payload)
      .then((response) => {
        console.log("🚀 ~ .then ~ response:", response);
        const newGuestId = response.data.guestId;
        if (newGuestId) {
          setGuestId(newGuestId);
          localStorage.setItem("guestId", newGuestId);
        }

        const newCurrentQuestionIndex = currentQuestionIndex + 1;
        setCurrentQuestionIndex(newCurrentQuestionIndex);
        const isQuestionnaireComplete =
          questions.length > 0 && newCurrentQuestionIndex >= questions.length;

        setMessages((prevMessages) => {
          let updatedMessages = prevMessages.filter(
            (msg) =>
              !(
                msg.type === "bot" &&
                msg.isLoading &&
                !msg.isRecommendationLoader &&
                !msg.isPdfGenerationLoader
              )
          ); // Keep specific loaders
          if (
            !isQuestionnaireComplete &&
            questions.length > 0 &&
            questions[newCurrentQuestionIndex]
          ) {
            updatedMessages.push({
              type: "bot",
              text: questions[newCurrentQuestionIndex].system,
              isStreaming: true,
            });
            // Scroll to show the new question after a brief delay
            setTimeout(() => {
              if (messagesEndRef.current) {
                messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
              }
            }, 100);
          }
          return updatedMessages;
        });

        if (isQuestionnaireComplete) {
          if (
            !recommendationInitiatedRef.current &&
            !isRecommending &&
            !isGeneratingPdf
          ) {
            recommendationInitiatedRef.current = true;
            fetchAndRecommendLesson(sessionIdToUse);
          }
        }
      })
      .catch((error) => {
        console.error("Error sending message to /chat:", error);
        const errorMessage =
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to send message.";
        toast.error(
          errorMessage.toLowerCase().includes("limit exceeded")
            ? "Your session has expired."
            : errorMessage
        );
        setMessages((prev) =>
          prev.filter(
            (msg) =>
              !(
                msg.type === "bot" &&
                msg.isLoading &&
                !msg.isRecommendationLoader &&
                !msg.isPdfGenerationLoader
              )
          )
        );
        recommendationInitiatedRef.current = false;
      })
      .finally(() => setIsLoading(false));
  };

  const fetchAndRecommendLesson = async (
    sessionId,
    skipInitialLoader = false
  ) => {
    if (!sessionId) return;

    // If lessons are already in messages (e.g., on page reload), just rehydrate state and PDF link if present
    if (skipInitialLoader && messages.some((m) => m.isRecommendation)) {
      // This rehydration logic needs to be robust if you want to fully restore state
      // It's safer to just re-fetch the recommendations if the full state isn't easily reconstructible
      // For now, if recommendedLesson is null, we can still try to set it from existing messages
      if (!recommendedLesson) {
        const recsFromMessages = messages
          .filter((m) => m.isRecommendation && !m.isBonusRecommendation) // Exclude bonus if you want separate state
          .map((m) => ({
            lessonNumber: m.lessonNumber,
            name: m.lessonName,
            link: m.linkUrl,
            // You'd need to store ALL original properties if you want full rehydration for PDF
            // For simplicity, we'll let the API re-fetch if recommendedLesson is null.
          }));
        if (recsFromMessages.length > 0) setRecommendedLesson(recsFromMessages);
      }
      if (!bonusLessons) {
        // Rehydrate bonus lessons
        const bonusRecsFromMessages = messages
          .filter((m) => m.isBonusRecommendation)
          .map((m) => ({
            lessonNumber: m.lessonNumber,
            name: m.lessonName,
            link: m.linkUrl,
            // Again, need all original properties for PDF
          }));
        if (bonusRecsFromMessages.length > 0)
          setBonusLessons(bonusRecsFromMessages);
      }
      // We don't re-trigger PDF download here if link is already in messages.
      return;
    }

    setIsRecommending(true);
    setMessages((prev) => [
      ...prev.filter(
        (msg) =>
          !(
            msg.type === "bot" &&
            msg.isLoading &&
            !msg.isRecommendationLoader &&
            !msg.isPdfGenerationLoader
          )
      ),
      {
        type: "bot",
        text: "Analyzing your responses and generating your plan...",
        isLoading: true,
        isStreaming: false,
        isRecommendationLoader: true,
      },
    ]);

    try {
      const convoResponse = await axios.get(
        `${API_BASE_URL_USER}/get-conversation/${sessionId}`
      );
      if (
        !convoResponse.data.responseData ||
        !convoResponse.data.responseData.conversation
      ) {
        throw new Error(
          "Failed to fetch conversation data for recommendation. The existing conversation might be incomplete or missing."
        );
      }
      const conversationForRecommendation =
        convoResponse.data.responseData.conversation.map((item) => ({
          system: item.system,
          user: item.user,
        }));
      const recommendPayload = {
        sessionId: sessionId,
        conversation: conversationForRecommendation,
        guestId: guestId,
        useProgress: true, // Enable progress updates
      };

      // Use fetch with streaming for SSE progress updates
      const response = await fetch(`${API_BASE_URL_USER}/recommend-lesson`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(recommendPayload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalData = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              // Update progress
              if (data.progress !== undefined) {
                setRecommendationProgress(data.progress);
                if (data.message) {
                  setProgressMessage(data.message);
                  // Update the message text with progress
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.isRecommendationLoader
                        ? { ...msg, text: data.message || msg.text }
                        : msg
                    )
                  );
                }
              }

              // Check if this is the final response
              if (data.progress === 100 && data.recommended_lessons) {
                finalData = data;
              }

              // Handle errors
              if (data.error) {
                throw new Error(data.error);
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }

      // Process final data
      if (!finalData) {
        throw new Error('No final data received from server');
      }

      setMessages((prev) => prev.filter((msg) => !msg.isRecommendationLoader)); // Remove recommendation loader
      setIsRecommending(false); // Recommendation part done
      setRecommendationProgress(0); // Reset progress
      setProgressMessage(""); // Reset message

      if (finalData) {
        let apiRecommendations = finalData.recommended_lessons || [];
        const apiBonusLessons = finalData.bonus_lessons || []; // GET BONUS LESSONS
        const userCriteriaForPdf = finalData.user_criteria_debug;

        // Filter recommendations based on lesson count settings by level
        const filteredRecommendations = [];
        const lessonsByLevel = {
          level1: [],
          level2: [],
          level3: [],
        };

        // Group lessons by level
        apiRecommendations.forEach((lesson) => {
          if (lesson.level === 1) lessonsByLevel.level1.push(lesson);
          else if (lesson.level === 2) lessonsByLevel.level2.push(lesson);
          else if (lesson.level === 3) lessonsByLevel.level3.push(lesson);
        });

        console.log("📊 Lessons by level:", {
          level1: lessonsByLevel.level1.length,
          level2: lessonsByLevel.level2.length,
          level3: lessonsByLevel.level3.length,
        });
        console.log("📊 Lesson count settings:", lessonCountSettings);

        // Apply count limits
        // If count is set (not null/undefined), use it (0 means show 0, >0 means show that many)
        // If count is null/undefined, show all lessons for that level
        
        // Level 1 filtering
        if (lessonCountSettings.l1LessonCount !== null && lessonCountSettings.l1LessonCount !== undefined) {
          const count = Number(lessonCountSettings.l1LessonCount);
          const lessonsToShow = lessonsByLevel.level1.slice(0, count);
          filteredRecommendations.push(...lessonsToShow);
          console.log(`📊 Level 1: Showing ${lessonsToShow.length} of ${lessonsByLevel.level1.length} lessons (limit: ${count})`);
          if (count > 0 && lessonsToShow.length < count && lessonsByLevel.level1.length < count) {
            console.warn(`⚠️ Level 1: Requested ${count} lessons, but only ${lessonsByLevel.level1.length} are available from API.`);
          }
        } else {
          filteredRecommendations.push(...lessonsByLevel.level1);
          console.log(`📊 Level 1: Showing all ${lessonsByLevel.level1.length} lessons (no limit)`);
        }

        // Level 2 filtering
        if (lessonCountSettings.l2LessonCount !== null && lessonCountSettings.l2LessonCount !== undefined) {
          const count = Number(lessonCountSettings.l2LessonCount);
          const lessonsToShow = lessonsByLevel.level2.slice(0, count);
          filteredRecommendations.push(...lessonsToShow);
          console.log(`📊 Level 2: Showing ${lessonsToShow.length} of ${lessonsByLevel.level2.length} lessons (limit: ${count})`);
          if (count > 0 && lessonsToShow.length < count && lessonsByLevel.level2.length < count) {
            console.warn(`⚠️ Level 2: Requested ${count} lessons, but only ${lessonsByLevel.level2.length} are available from API.`);
          }
        } else {
          filteredRecommendations.push(...lessonsByLevel.level2);
          console.log(`📊 Level 2: Showing all ${lessonsByLevel.level2.length} lessons (no limit)`);
        }

        // Level 3 filtering
        if (lessonCountSettings.l3LessonCount !== null && lessonCountSettings.l3LessonCount !== undefined) {
          const count = Number(lessonCountSettings.l3LessonCount);
          const lessonsToShow = lessonsByLevel.level3.slice(0, count);
          filteredRecommendations.push(...lessonsToShow);
          console.log(`📊 Level 3: Showing ${lessonsToShow.length} of ${lessonsByLevel.level3.length} lessons (limit: ${count})`);
          if (count > 0 && lessonsToShow.length < count && lessonsByLevel.level3.length < count) {
            console.warn(`⚠️ Level 3: Requested ${count} lessons, but only ${lessonsByLevel.level3.length} are available from API.`);
          }
        } else {
          filteredRecommendations.push(...lessonsByLevel.level3);
          console.log(`📊 Level 3: Showing all ${lessonsByLevel.level3.length} lessons (no limit)`);
        }

        console.log("📊 Original recommendations:", apiRecommendations.length);
        console.log("📊 Filtered recommendations:", filteredRecommendations.length);

        apiRecommendations = filteredRecommendations; // Use filtered recommendations

        setRecommendedLesson(apiRecommendations); // Store filtered recommendations for PDF payload
        setBonusLessons(apiBonusLessons); // STORE BONUS LESSONS

        if (apiRecommendations.length === 0 && apiBonusLessons.length === 0) {
          setMessages((prev) => [
            ...prev,
            {
              type: "bot",
              text:
                finalData.message ||
                "No lessons match your specific criteria. Please try adjusting your preferences.",
              isStreaming: false,
            },
          ]);
        } else {
          // Show L1 intro text if available, otherwise show default message
          const introText = chatTextSettings.l1IntroText.trim() || "Great! Based on your answers, here are some top recommendations for you:";
          setMessages((prev) => [
            ...prev,
            {
              type: "bot",
              text: introText,
              isStreaming: false,
              isL1IntroText: true,
            },
          ]);

          // Group lessons by level for proper insertion of explanation texts
          const lessonsByLevel = {
            level1: [],
            level2: [],
            level3: [],
          };

          apiRecommendations.forEach((rec) => {
            if (rec.level === 1) lessonsByLevel.level1.push(rec);
            else if (rec.level === 2) lessonsByLevel.level2.push(rec);
            else if (rec.level === 3) lessonsByLevel.level3.push(rec);
          });

          // Display Level 1 lessons
          lessonsByLevel.level1.forEach((rec, index) => {
            setMessages((prev) => [
              ...prev,
              {
                type: "bot",
                displayText: null,
                linkUrl: rec.link,
                isRecommendation: true,
                lessonNumber: rec.lessonNumber,
                lessonName: rec.name,
                lessonSummary: rec.summary || rec.Summary || null, // Store summary (handle both cases)
                theme: rec.theme,
                level: 1,
                isStreaming: false,
              },
            ]);
          });

          // Show L2 explanation if there are L2 lessons and explanation text exists
          if (lessonsByLevel.level2.length > 0 && chatTextSettings.l2Explanation.trim()) {
            setMessages((prev) => [
              ...prev,
              {
                type: "bot",
                text: chatTextSettings.l2Explanation,
                isStreaming: false,
                isLevelExplanation: true,
                explanationLevel: 2,
              },
            ]);
          }

          // Display Level 2 lessons
          lessonsByLevel.level2.forEach((rec, index) => {
            setMessages((prev) => [
              ...prev,
              {
                type: "bot",
                displayText: null,
                linkUrl: rec.link,
                isRecommendation: true,
                lessonNumber: rec.lessonNumber,
                lessonName: rec.name,
                lessonSummary: rec.summary || rec.Summary || null, // Store summary (handle both cases)
                theme: rec.theme,
                level: 2,
                isStreaming: false,
              },
            ]);
          });

          // Show L3 explanation if there are L3 lessons and explanation text exists
          if (lessonsByLevel.level3.length > 0 && chatTextSettings.l3Explanation.trim()) {
            setMessages((prev) => [
              ...prev,
              {
                type: "bot",
                text: chatTextSettings.l3Explanation,
                isStreaming: false,
                isLevelExplanation: true,
                explanationLevel: 3,
              },
            ]);
          }

          // Display Level 3 lessons
          lessonsByLevel.level3.forEach((rec, index) => {
            setMessages((prev) => [
              ...prev,
              {
                type: "bot",
                displayText: null,
                linkUrl: rec.link,
                isRecommendation: true,
                lessonNumber: rec.lessonNumber,
                lessonName: rec.name,
                lessonSummary: rec.summary || rec.Summary || null, // Store summary (handle both cases)
                theme: rec.theme,
                level: 3,
                isStreaming: false,
              },
            ]);
          });

          // --- DISPLAY BONUS LESSONS ---
          if (apiBonusLessons.length > 0) {
            // Use custom bonus header text if available, otherwise use default
            const bonusHeaderText = chatTextSettings.bonusLessonsHeaderText.trim() || 
              "If you took the special offer for the extra 12 lessons, here are some bonus options for you. If you didn't, and you've changed your mind, you can still get access below.";
            setMessages((prev) => [
              ...prev,
              {
                type: "bot",
                text: bonusHeaderText,
                isStreaming: false,
                isBonusHeader: true,
              },
            ]); // Custom flag for styling
            apiBonusLessons.forEach((rec, index) => {
              // Don't store displayText - always regenerate based on current setting
              setMessages((prev) => [
                ...prev,
                {
                  type: "bot",
                  // Store null for displayText - we'll regenerate in render based on current setting
                  displayText: null,
                  linkUrl: rec.link,
                  isRecommendation: true,
                  isBonusRecommendation: true, // Specific flag for bonus lessons
                  lessonNumber: rec.lessonNumber,
                  lessonName: rec.name,
                  lessonSummary: rec.summary || rec.Summary || null, // Store summary (handle both cases)
                  theme: rec.theme,
                  isStreaming: false,
                },
              ]);
            });
          }

          // --- DISPLAY FINAL CHAT TEXT ---
          if (chatTextSettings.finalChatText.trim()) {
            setMessages((prev) => [
              ...prev,
              {
                type: "bot",
                text: chatTextSettings.finalChatText,
                isStreaming: false,
                isFinalChatText: true,
              },
            ]);
          }

          // --- DISPLAY FINAL REDIRECT URL ---
          if (chatTextSettings.finalRedirectUrl && chatTextSettings.finalRedirectUrl.trim()) {
            setMessages((prev) => [
              ...prev,
              {
                type: "bot",
                text: chatTextSettings.finalRedirectUrl,
                isStreaming: false,
                isRedirectUrl: true,
                redirectUrl: chatTextSettings.finalRedirectUrl,
              },
            ]);
          }
        }

        // ---- PDF DOWNLOAD LOGIC ----
        // Only attempt PDF if enablePdfDownload is true AND there are main recommendations OR bonus recommendations
        if (
          enablePdfDownload &&
          userCriteriaForPdf &&
          (apiRecommendations.length > 0 || apiBonusLessons.length > 0)
        ) {
          setIsGeneratingPdf(true);
          setMessages((prev) => [
            ...prev,
            {
              type: "bot",
              text: "Generating your personalized CashFlow Plan PDF...",
              isLoading: true,
              isStreaming: false,
              isPdfGenerationLoader: true,
            },
          ]);

          try {
            const pdfPayload = {
              userCriteria: userCriteriaForPdf,
              recommendedLessons: apiRecommendations, // Send full recommendations
              bonusLessons: apiBonusLessons, // SEND BONUS LESSONS TO PDF GENERATOR
            };
            const pdfRes = await axios.post(
              `${API_BASE_URL_USER}/download-cashflow-plan`,
              pdfPayload
            );

            setMessages((prev) =>
              prev.filter((msg) => !msg.isPdfGenerationLoader)
            );

            if (pdfRes.data && pdfRes.data.pdf_url) {
              const pdfDownloadUrl = pdfRes.data.pdf_url;
              // Use custom PDF download message if available, otherwise use default
              const pdfMessage = chatTextSettings.pdfDownloadMessage.trim() || 
                "You can view your full personalized CashFlow Plan PDF.";
              setMessages((prev) => [
                ...prev,
                {
                  type: "bot",
                  text: pdfMessage,
                  isStreaming: false,
                },
              ]);
              setMessages((prev) => [
                ...prev,
                {
                  type: "bot",
                  text: `[Download Your CashFlow Plan PDF](${pdfDownloadUrl})`,
                  isPdfLink: true,
                  pdfLinkUrl: pdfDownloadUrl,
                  isStreaming: false,
                },
              ]);
            } else {
              console.warn(
                "PDF download API call succeeded but no pdf_url was returned.",
                pdfRes.data
              );
              setMessages((prev) => [
                ...prev,
                {
                  type: "bot",
                  text: "\nA detailed PDF plan could not be generated at this moment (no URL received).",
                  isStreaming: false,
                  isError: true,
                },
              ]);
            }
          } catch (pdfError) {
            console.error("Error calling /download-cashflow-plan:", pdfError);
            setMessages((prev) =>
              prev.filter((msg) => !msg.isPdfGenerationLoader)
            );
            const pdfErrorMessage =
              pdfError.response?.data?.message ||
              pdfError.message ||
              "Failed to generate PDF plan.";
            setMessages((prev) => [
              ...prev,
              {
                type: "bot",
                text: `\nError generating PDF: ${pdfErrorMessage}`,
                isStreaming: false,
                isError: true,
              },
            ]);
          } finally {
            setIsGeneratingPdf(false);
          }
        } else if (!enablePdfDownload) {
          // PDF download is disabled - don't show any message, just silently skip
          console.log("PDF download is disabled in settings. Skipping PDF generation.");
        } else {
          // PDF is enabled but missing criteria or recommendations
          setMessages((prev) => [
            ...prev,
            {
              type: "bot",
              text: "\nCould not generate PDF plan due to missing criteria or recommendations.",
              isStreaming: false,
              isError: true,
            },
          ]);
        }
      } else {
        // No data from finalData or other unexpected response
        let errorMessage =
          "Sorry, I couldn't find any specific lesson recommendations for you right now.";
        if (finalData?.message)
          errorMessage = finalData.message;
        setMessages((prev) => [
          ...prev,
          {
            type: "bot",
            text: errorMessage,
            isStreaming: false,
            isError: true,
          },
        ]);
        setRecommendedLesson([]);
        setBonusLessons([]); // Ensure bonus lessons are also cleared
      }
    } catch (err) {
      console.error("Error during lesson recommendation:", err);
      const errorMsg =
        err.response?.data?.message ||
        err.message ||
        "Could not recommend lessons.";
      toast.error(errorMsg);
      setMessages((prev) => [
        ...prev.filter((msg) => !msg.isRecommendationLoader),
        {
          type: "bot",
          text: `Error generating recommendations: ${errorMsg}`,
          isStreaming: false,
          isError: true,
        },
      ]);
      setRecommendedLesson([]);
      setBonusLessons([]); // Ensure bonus lessons are also cleared on error
      setIsRecommending(false); // Ensure this is reset on error
      setRecommendationProgress(0); // Reset progress on error
      setProgressMessage(""); // Reset message on error
    }
  };

  useEffect(() => {
    if (autoScroll && messagesEndRef.current) {
      // Use requestAnimationFrame to ensure DOM is updated before scrolling
      requestAnimationFrame(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      });
    }
    setIsMessageChanged(Date.now().toString());
  }, [messages, autoScroll, enableLessonHyperlinks]); // Re-render when hyperlinks setting changes

  // Ensure first message is visible on initial load
  useEffect(() => {
    if (messages && messages.length > 0 && scrollContainerRef.current) {
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = 0;
        }
      }, 100);
    }
  }, [termsAccepted]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    const handleScroll = () => {
      if (!container) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollHeight - scrollTop <= clientHeight + 30;
      setAutoScroll(isAtBottom);
    };
    if (container) {
      container.addEventListener("scroll", handleScroll);
      setTimeout(handleScroll, 0);
    }
    return () => {
      if (container) container.removeEventListener("scroll", handleScroll);
    };
  }, [isMessageChanged]);

  if (!termsAccepted) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 p-4">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              backgroundRepeat: "repeat",
            }}
          ></div>
        </div>

        <div className="relative z-10 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-2xl max-w-4xl w-full shadow-2xl border border-white/20 dark:border-gray-700/50">
          {/* Header */}
          <div className="p-6 border-b border-gray-200/50 dark:border-gray-700/50">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                <FaShieldAlt className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                  SmartStart AI
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Terms & Conditions
                </p>
              </div>
            </div>
          </div>

          {/* Terms Content */}
          <div className="p-6 max-h-96 overflow-y-auto">
            <div
              className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: termsAndConditionsText }}
            />
          </div>

          {/* Action Buttons */}
          <div className="p-6 border-t border-gray-200/50 dark:border-gray-700/50">
            <button
              onClick={handleAcceptTerms}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              I Agree & Continue
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (isLoadingQuestions && termsAccepted) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-blue-900 dark:to-indigo-900 p-4">
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl p-8 rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 text-center max-w-sm w-full">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <svg
              className="w-8 h-8 text-white animate-spin"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            SmartStart AI
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Loading your experience...
          </p>
          <div className="flex items-center justify-center space-x-1">
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
            <div
              className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"
              style={{ animationDelay: "0.1s" }}
            ></div>
            <div
              className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"
              style={{ animationDelay: "0.2s" }}
            ></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-140px)] overflow-y-auto bg-gradient-to-r from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-blue-900 dark:to-indigo-900">
      {/* Chat Container - Fixed height with proper scroll */}
      <div
        className="flex-1 flex   flex-col relative "
        style={{
          //   minHeight: "calc(100vh - 300px)",
          willChange: "auto",
          contain: "layout",
        }}
      >
        <div
          ref={scrollContainerRef}
          className="flex-1 px-4 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6 "
          style={{
            scrollBehavior: "smooth",
            // height: "100%",
            // overflowY: "auto",
            paddingTop: "20px",
            paddingBottom: "90px", // Increased padding to account for fixed input area
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain",
          }}
        >
          {messages?.map((msg, i) => (
            <div
              key={i}
              className={`flex ${
                msg.type === "user" ? "justify-end" : "justify-start"
              } mb-4 sm:mb-6 ${i === 0 ? "mt-4" : ""}`}
            >
              <div
                className={`gap-[10px] flex items-start  max-w-[85%] sm:max-w-[80%] ${
                  msg.type === "user" ? "flex-row-reverse space-x-reverse" : ""
                }`}
              >
                {/* Avatar */}
                <div
                  className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center ${
                    msg.type === "user"
                      ? "bg-gradient-to-r from-blue-500 to-blue-600"
                      : "bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600"
                  }`}
                >
                  {msg.type === "user" ? (
                    <svg
                      className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-600 dark:text-gray-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                      />
                    </svg>
                  )}
                </div>

                {/* Message Content */}
                <div
                  className={`flex flex-col ${
                    msg.type === "user" ? "items-end" : "items-start"
                  }`}
                >
                  <div
                    className={`px-3 sm:px-4 py-2 sm:py-3 rounded-2xl shadow-sm transition-all duration-200 text-sm sm:text-base ${
                      msg.type === "user"
                        ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-br-md shadow-lg"
                        : msg.isError
                        ? "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300"
                        : "bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 rounded-bl-md shadow-md"
                    }`}
                    style={{
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      whiteSpace: "normal",
                    }}
                  >
                    {msg.type === "user" ? (
                      msg.text
                    ) : (
                      <>
                        <Loader
                          status={
                            (msg.isLoading &&
                              !msg.isRecommendationLoader &&
                              !msg.isPdfGenerationLoader) ||
                            false
                          }
                        />
                        {!msg.isLoading &&
                          (msg.isStreaming &&
                          !msg.isRecommendation &&
                          !msg.isPdfLink &&
                          !msg.isBonusHeader &&
                          !msg.isRedirectUrl ? ( // Exclude bonus header and redirect URL from StreamText
                            // Check if message text contains HTML
                            containsHTML(msg.text ?? "") ? (
                              // Render HTML directly (same as QuestionList.js)
                              <div className="ql-snow">
                                <div 
                                  className="ql-editor !p-0 text-sm font-medium text-gray-900 dark:text-white break-words"
                                  style={{ minHeight: 'unset' }}
                                  dangerouslySetInnerHTML={{ __html: formatHtmlForDisplay(msg.text ?? "") }} 
                                />
                              </div>
                            ) : (
                              // Stream non-HTML text
                              <StreamText
                                setIsLoading={setIsLoading}
                                setIsMessageChanged={setIsMessageChanged}
                                fullText={msg.text ?? ""}
                              />
                            )
                          ) : msg.isRecommendation && msg.linkUrl ? (
                            // Always regenerate display based on current setting, ignore stored displayText
                            // Use console.log to debug the current setting value
                            (() => {
                              console.log("🔗 Rendering recommendation - enableLessonHyperlinks:", enableLessonHyperlinks, "showSummaryBelowTitle:", showSummaryBelowTitle, "lessonName:", msg.lessonName);
                              
                              // Format: "Lesson Name - Summary" (only if showSummaryBelowTitle is true)
                              const lessonName = msg.lessonName || 'Lesson';
                              const lessonSummary = msg.lessonSummary || '';
                              const displayText = (showSummaryBelowTitle && lessonSummary) 
                                ? `${lessonName} - ${lessonSummary}`
                                : lessonName;
                              
                              return enableLessonHyperlinks ? (
                                // Differentiate bonus lessons visually if desired, e.g., different color
                                msg.isBonusRecommendation ? (
                                  <a
                                    href={msg.linkUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-purple-600 dark:text-purple-400 underline hover:text-purple-800 dark:hover:text-purple-200 font-semibold transition-colors duration-200 flex items-center gap-1"
                                    aria-label={`Bonus lesson: ${lessonName}`}
                                  >
                                    <span className="font-medium">{displayText}</span>
                                    <svg
                                      className="w-4 h-4 flex-shrink-0"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                      />
                                    </svg>
                                  </a>
                                ) : (
                                  // Regular recommendation
                                  <a
                                    href={msg.linkUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-200 font-semibold transition-colors duration-200 flex items-center gap-1"
                                    aria-label={`Recommended lesson: ${lessonName}`}
                                  >
                                    <span className="font-medium">{displayText}</span>
                                    <svg
                                      className="w-4 h-4 flex-shrink-0"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                      />
                                    </svg>
                                  </a>
                                )
                              ) : (
                                // Hyperlinks disabled - show plain text
                                <span className="font-medium text-gray-900 dark:text-white">
                                  {displayText}
                                </span>
                              );
                            })()
                          ) : msg.isPdfLink && msg.pdfLinkUrl ? (
                            <a
                              href={msg.pdfLinkUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 dark:text-blue-400 hover:underline font-semibold"
                            >
 
      {msg.text.match(/\[(.*?)\]/)?.[1] || "View PDF Plan"}
    </a>
) : msg.isL1IntroText || msg.isLevelExplanation || msg.isBonusHeader || msg.isFinalChatText ? (
    /* 2. RICH TEXT / QUILL MESSAGES (Fixes ql-size-huge issues) */
    msg.text && msg.text.trim() ? (
        <div className="ql-snow"> 
            <div
                className={`ql-editor ${msg.isBonusHeader ? 'font-bold' : ''} text-gray-900 dark:text-white`}
                style={{ 
                    marginTop: msg.isBonusHeader ? "20px" : "0", 
                    padding: 0,           // Removes Quill's default internal padding
                    minHeight: 'unset',    // Prevents Quill from forcing a 150px height
                    background: 'transparent' 
                }}
                dangerouslySetInnerHTML={{ __html: formatHtmlForDisplay(msg.text) }}
            />
        </div>
    ) : (
        <span className="text-gray-900 dark:text-white">{msg.text}</span>
    )
) : msg.isRedirectUrl && msg.redirectUrl ? (
    /* 3. CONTINUE / REDIRECT BUTTON */
    <a
      href={msg.redirectUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-block mt-3 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
    >
      Continue →
    </a>
) : (
    /* 4. FALLBACK FOR STANDARD TEXT */
    msg.text
 ))}
                        {(msg.isRecommendationLoader ||
                          msg.isPdfGenerationLoader) &&
                          msg.isLoading && (
                            <>
                              {msg.isRecommendationLoader && recommendationProgress > 0 ? (
                                <div className="w-full">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                      {progressMessage || msg.text}
                                    </span>
                                    <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                                      {recommendationProgress}%
                                    </span>
                                  </div>
                                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                                    <div
                                      className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                                      style={{ width: `${recommendationProgress}%` }}
                                    ></div>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <Loader
                                    status={true}
                                    size="small"
                                    inline={true}
                                  />
                                  <span style={{ marginLeft: "8px" }}>
                                    {msg.text}
                                  </span>
                                </>
                              )}
                            </>
                          )}
                      </>
                    )}
                  </div>

                  {/* Timestamp */}
                  <div
                    className={`text-xs text-gray-500 dark:text-gray-400 mt-1 ${
                      msg.type === "user" ? "text-right" : "text-left"
                    }`}
                  >
                    {new Date().toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start mb-4 sm:mb-6">
              <div className="flex items-start space-x-2 sm:space-x-3 max-w-[85%] sm:max-w-[80%]">
                {/* AI Avatar */}
                <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600">
                  <svg
                    className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-600 dark:text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                    />
                  </svg>
                </div>

                {/* Typing Indicator */}
                <div className="bg-white dark:bg-gray-800 px-3 sm:px-4 py-2 sm:py-3 rounded-2xl rounded-bl-md shadow-md border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center space-x-2">
                    <div className="flex space-x-1">
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full animate-bounce"></div>
                      <div
                        className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full animate-bounce"
                        style={{ animationDelay: "0.1s" }}
                      ></div>
                      <div
                        className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full animate-bounce"
                        style={{ animationDelay: "0.2s" }}
                      ></div>
                    </div>
                    <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      AI is thinking...
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Scroll to bottom button */}
        {!autoScroll && (
          <button
            onClick={() => {
              if (messagesEndRef.current) {
                messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
                setAutoScroll(true);
              }
            }}
            className="absolute bottom-4 right-4 bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-full shadow-lg transition-all duration-200 z-20"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Input Area - Fixed at bottom */}
      <div className="fixed bottom-0 left-0 right-0 w-full bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl border-t border-gray-200/50 dark:border-gray-700/50 p-4 sm:p-6 flex-shrink-0 z-10">
        <div className="flex items-start space-x-2 sm:space-x-3">
          <div className="flex-1 relative">
            <textarea
              placeholder={
                isLoading
                  ? "Waiting..."
                  : isRecommending
                  ? "Analyzing responses..."
                  : isGeneratingPdf
                  ? "Generating PDF..."
                  : recommendedLesson && recommendedLesson.length > 0
                  ? "Your lesson plan is ready. See above or download the PDF."
                  : questions.length > 0 &&
                    currentQuestionIndex < questions.length &&
                    messages?.some(
                      (m) =>
                        m.type === "bot" &&
                        !m.isLoading &&
                        m.text === questions[currentQuestionIndex]?.system
                    )
                  ? "Your answer..."
                  : termsAccepted &&
                    questions.length === 0 &&
                    !isLoadingQuestions
                  ? "No questions loaded. Try refreshing."
                  : questions.length > 0 &&
                    currentQuestionIndex >= questions.length &&
                    !isRecommending &&
                    !isGeneratingPdf &&
                    (!recommendedLesson || recommendedLesson.length === 0)
                  ? "All questions answered. Waiting for plan..."
                  : "Type your message..."
              }
              className={`w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-2 resize-none ${
                messages.find(
                  (m) =>
                    m.isError &&
                    m.type === "bot" &&
                    messages.indexOf(m) === messages.length - 1
                )
                  ? "border-red-400 focus:ring-red-400 focus:border-red-400"
                  : "border-gray-200 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500"
              } rounded-xl sm:rounded-2xl px-3 sm:px-4 py-2 sm:py-3 pr-10 sm:pr-12 focus:outline-none transition-all duration-200 shadow-sm hover:shadow-md text-sm sm:text-base focus:shadow-lg`}
              value={input}
              disabled={
                isLoading ||
                isRecommending ||
                isGeneratingPdf ||
                !termsAccepted ||
                (termsAccepted && isLoadingQuestions) ||
                (currentQuestionIndex >= questions.length &&
                  !isRecommending &&
                  !isGeneratingPdf &&
                  (!recommendedLesson || recommendedLesson.length === 0)) ||
                (recommendedLesson && recommendedLesson.length > 0)
              }
              onChange={(e) => {
                setInput(e.target.value);
                // Auto-resize textarea smoothly with better browser compatibility
                const textarea = e.target;
                textarea.style.height = "auto";
                const newHeight = Math.min(textarea.scrollHeight, 120);
                textarea.style.height = newHeight + "px";

                // Force reflow for better browser compatibility
                void textarea.offsetHeight;
              }}
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  !e.shiftKey &&
                  !isLoading &&
                  !isRecommending &&
                  !isGeneratingPdf &&
                  input.trim() &&
                  !(recommendedLesson && recommendedLesson.length > 0)
                ) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              rows={1}
              style={{
                minHeight: "40px",
                maxHeight: "120px",
                resize: "none",
                overflow: "hidden",
                transition: "height 0.2s ease",
              }}
            />
            <div className="absolute right-2 sm:right-3 top-2 sm:top-3">
              <svg
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
            </div>
          </div>

          <button
            disabled={
              isLoading ||
              isRecommending ||
              isGeneratingPdf ||
              !input.trim() ||
              !termsAccepted ||
              (termsAccepted && isLoadingQuestions) ||
              (currentQuestionIndex >= questions.length &&
                !isRecommending &&
                !isGeneratingPdf &&
                (!recommendedLesson || recommendedLesson.length === 0)) ||
              (recommendedLesson && recommendedLesson.length > 0)
            }
            onClick={handleSend}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white p-2 sm:p-3 rounded-xl sm:rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none flex items-center justify-center min-w-[40px] sm:min-w-[48px] h-[40px] sm:h-[48px] self-start"
          >
            {isLoading || isRecommending || isGeneratingPdf ? (
              <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <svg
                className="w-4 h-4 sm:w-5 sm:h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                />
              </svg>
            )}
          </button>
        </div>

        {/* Input Helper Text */}
        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 text-center hidden sm:block">
          Press Enter to send, Shift+Enter for new line
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
